// Chart notes under the Overview chart: the notes in range as a short list, and (for admins and
// the project's owners) an editor to add, change or delete them. The chart draws the markers
// (chart.ts); this module keeps the list and calls `redraw` when it changes.
import { NOTE_CATEGORIES, type ChartNote, type NoteCategory } from "../src/types";
import { apiUrl } from "./share";

export interface NotesCtx {
  project: string;
  /** The chart's first and last day (YYYY-MM-DD). */
  from: string;
  to: string;
  canEdit: boolean;
  /** Redraws the chart and this list from `currentNotes()`. */
  redraw: () => void;
  current: () => boolean;
}

const CAT_LABEL: Record<NoteCategory, string> = { note: "Note", launch: "Launch", campaign: "Campaign", incident: "Incident" };
const PROBLEMS: Record<string, string> = {
  bad_text: "Write a short note, up to 140 characters.",
  bad_day: "Pick a date.",
  bad_category: "Pick a type.",
  read_only: "You have view-only access to this project. Ask an owner to add notes.",
};
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (s: string) => `${Number(s.slice(8, 10))} ${MON[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}`;
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

let notes: ChartNote[] = [];
let ctx: NotesCtx | null = null;
/** The open editor: a new note for `day`, or the note with `id`. */
let editing: { id?: string; day: string } | null = null;
let confirmDelete: string | null = null;

export const currentNotes = () => notes;

/** The project's notes between two days. Notes are extra: failures give an empty list. */
export async function fetchNotes(project: string, from: string, to: string): Promise<ChartNote[]> {
  try {
    const res = await fetch(`${apiUrl("notes")}?${new URLSearchParams({ project, from, to })}`, { credentials: "same-origin" });
    return res.ok ? ((await res.json()) as ChartNote[]) : [];
  } catch {
    return [];
  }
}

/** Starts a screen's notes: keeps `list` and forgets any editor left open on another project. */
export function initNotes(c: NotesCtx, list: ChartNote[]): void {
  if (ctx?.project !== c.project) editing = confirmDelete = null;
  ctx = c;
  notes = list;
}

/** Opens the editor for a new note on `day` (a click on the chart). */
export function addNoteOn(day: string): void {
  if (!ctx?.canEdit) return;
  editing = { day };
  confirmDelete = null;
  renderNotes();
  document.querySelector<HTMLInputElement>('[data-note-form] [name="text"]')?.focus();
}

/** Fills #notes. Nothing at all for read-only viewers when the range has no notes. */
export function renderNotes(): void {
  const host = document.getElementById("notes");
  if (!host || !ctx) return;
  const c = ctx;
  if (!notes.length && !c.canEdit) return void (host.innerHTML = "");
  const editor = editing && c.canEdit ? form(c, editing) : "";
  const rows = notes
    .map((n) => {
      if (editing?.id === n.id) return "";
      const acts = !c.canEdit
        ? ""
        : confirmDelete === n.id
          ? `<span class="note-acts"><button class="link danger" data-note-act="delete-yes" data-id="${n.id}">Delete</button><button class="link" data-note-act="cancel">Keep</button></span>`
          : `<span class="note-acts"><button class="link" data-note-act="edit" data-id="${n.id}">Edit</button><button class="link" data-note-act="delete" data-id="${n.id}">Delete</button></span>`;
      return `<li class="note-row"><i class="ndot cat-${n.category}" title="${CAT_LABEL[n.category]}"></i>
        <span class="note-main"><span class="note-text">${esc(n.text)}</span><span class="note-meta">${dayLabel(n.day)} · ${CAT_LABEL[n.category]}${n.created_by ? ` · ${esc(n.created_by)}` : ""}</span></span>${acts}</li>`;
    })
    .join("");
  host.innerHTML = `<div class="notes-head"><h3>Notes</h3>${
    c.canEdit && !editing ? `<button class="link" data-note-act="new">Add note</button>` : ""
  }</div>${editor}${
    notes.length ? `<ul class="note-list">${rows}</ul>` : editing ? "" : `<p class="notes-empty">Mark a launch, campaign or outage so the chart explains itself.</p>`
  }`;
}

function form(c: NotesCtx, e: { id?: string; day: string }): string {
  const n = e.id ? notes.find((x) => x.id === e.id) : undefined;
  const cat = n?.category ?? "note";
  return `<form class="editor note-form" data-note-form data-id="${n?.id ?? ""}">
    <div class="two-fld">
      <label class="fld narrow-date"><span>Date</span><input class="in" type="date" name="day" required min="${c.from}" max="${c.to}" value="${n?.day ?? e.day}"></label>
      <label class="fld narrow-cat"><span>Type</span><select class="in" name="category">${NOTE_CATEGORIES.map(
        (k) => `<option value="${k}"${k === cat ? " selected" : ""}>${CAT_LABEL[k]}</option>`,
      ).join("")}</select></label>
    </div>
    <label class="fld"><span>Note <span class="note-count" aria-live="polite"></span></span><input class="in" name="text" required maxlength="140" autocomplete="off" value="${esc(n?.text ?? "")}" placeholder="e.g. New menu launched"></label>
    <div class="form-err" hidden></div>
    <div class="btns"><button class="btn primary" type="submit">${n ? "Save" : "Add note"}</button><button class="btn" type="button" data-note-act="cancel">Cancel</button></div>
  </form>`;
}

async function send(method: "POST" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<void> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
}

/** After a change: re-read the range, then redraw the chart and list. */
async function reload(): Promise<void> {
  const c = ctx;
  if (!c) return;
  const list = await fetchNotes(c.project, c.from, c.to);
  if (ctx !== c || !c.current()) return;
  notes = list;
  c.redraw();
}

document.addEventListener("click", (ev) => {
  const b = (ev.target as HTMLElement).closest<HTMLElement>("[data-note-act]");
  if (!b || !ctx) return;
  const id = b.dataset.id;
  const act = b.dataset.noteAct;
  confirmDelete = null;
  if (act === "new") return addNoteOn(ctx.to);
  if (act === "edit" && id) editing = { id, day: notes.find((n) => n.id === id)?.day ?? ctx.to };
  else if (act === "cancel") editing = null;
  else if (act === "delete" && id) confirmDelete = id;
  else if (act === "delete-yes" && id) {
    void send("DELETE", `notes/${id}`)
      .then(reload)
      .catch((e) => alert(PROBLEMS[(e as Error).message] ?? `Couldn't delete: ${(e as Error).message}`));
    return;
  }
  renderNotes();
  if (act === "edit") document.querySelector<HTMLInputElement>('[data-note-form] [name="text"]')?.focus();
});

document.addEventListener("input", (ev) => {
  const t = ev.target as HTMLInputElement;
  if (!t.matches('[data-note-form] [name="text"]')) return;
  const left = 140 - [...t.value].length;
  const out = t.form?.querySelector(".note-count");
  if (out) out.textContent = left <= 20 ? `· ${left} left` : "";
});

document.addEventListener("submit", (ev) => {
  const f = (ev.target as HTMLElement).closest<HTMLFormElement>("form[data-note-form]");
  if (!f || !ctx) return;
  ev.preventDefault();
  const data = new FormData(f);
  const id = f.dataset.id || undefined;
  const body = { project: ctx.project, day: String(data.get("day") ?? ""), text: String(data.get("text") ?? ""), category: String(data.get("category") ?? "note") };
  const err = f.querySelector<HTMLElement>(".form-err")!;
  const btn = f.querySelector<HTMLButtonElement>('[type="submit"]')!;
  btn.disabled = true;
  void (id ? send("PATCH", `notes/${id}`, body) : send("POST", "notes", body))
    .then(() => {
      editing = null;
      return reload();
    })
    .catch((e) => {
      err.textContent = PROBLEMS[(e as Error).message] ?? `Couldn't save: ${(e as Error).message}`;
      err.hidden = false;
      btn.disabled = false;
    });
});
